/**
 * Sprint DS B10-B11: Keyboard Shortcut System
 *
 * Two-key chord sequences:
 * - G+key: Navigation (G D=Dashboard, G C=Contacts, etc.)
 * - N+key: Create (N C=Contact, N D=Deal, etc.)
 * - ?: Show shortcut overlay
 *
 * MUST NOT conflict with browser shortcuts.
 * Uses prefix key (G/N) held for 1.5s window.
 */
import { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";

interface ShortcutDef {
  key: string;
  label: string;
  action: string; // route path or "create:entity"
}

export const NAV_SHORTCUTS: ShortcutDef[] = [
  { key: "d", label: "Dashboard", action: "/" },
  { key: "c", label: "Contacts", action: "/crm/contacts" },
  { key: "e", label: "Email", action: "/email" },
  { key: "p", label: "Pages", action: "/pages" },
  { key: "w", label: "Workflows", action: "/workflows" },
  { key: "i", label: "Inbox", action: "/inbox" },
  { key: "s", label: "Services", action: "/services" },
  { key: "n", label: "Webinars", action: "/webinars" },
  { key: "a", label: "Analytics", action: "/analytics" },
  { key: "r", label: "Reputation", action: "/reputation" },
  { key: "b", label: "Briefing", action: "/briefing" },
];

export const CREATE_SHORTCUTS: ShortcutDef[] = [
  { key: "c", label: "Contact", action: "create:contact" },
  { key: "d", label: "Deal", action: "create:deal" },
  { key: "e", label: "Campaign", action: "create:campaign" },
  { key: "p", label: "Page", action: "create:page" },
  { key: "w", label: "Workflow", action: "create:workflow" },
  { key: "t", label: "Task", action: "create:task" },
  { key: "b", label: "Booking", action: "create:booking" },
];

type PrefixKey = "g" | "n" | null;

export function useKeyboardShortcuts(onShowOverlay: () => void) {
  const navigate = useNavigate();
  const prefixRef = useRef<PrefixKey>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [activePrefix, setActivePrefix] = useState<PrefixKey>(null);

  const clearPrefix = useCallback(() => {
    prefixRef.current = null;
    setActivePrefix(null);
    setShowHint(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't capture when typing in inputs
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (e.target as HTMLElement).isContentEditable) {
        return;
      }

      const key = e.key.toLowerCase();

      if ((e.metaKey || e.ctrlKey) && key === "j") {
        e.preventDefault();
        clearPrefix();
        window.dispatchEvent(new CustomEvent("ease:focus-ai-bar"));
        return;
      }

      // Don't capture with modifiers (browser shortcuts)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // ? → show overlay
      if (key === "?" || (e.shiftKey && key === "/")) {
        e.preventDefault();
        onShowOverlay();
        return;
      }

      // Escape → clear prefix
      if (key === "escape") {
        clearPrefix();
        return;
      }

      // Set prefix key
      if (key === "g" || key === "n") {
        if (prefixRef.current === null) {
          prefixRef.current = key as PrefixKey;
          setActivePrefix(key as PrefixKey);
          setShowHint(true);

          // Auto-clear after 1.5s
          timerRef.current = setTimeout(clearPrefix, 1500);
          e.preventDefault();
          return;
        }
      }

      // Execute chord
      if (prefixRef.current) {
        e.preventDefault();
        const prefix = prefixRef.current;
        clearPrefix();

        if (prefix === "g") {
          const shortcut = NAV_SHORTCUTS.find((s) => s.key === key);
          if (shortcut) navigate(shortcut.action);
        } else if (prefix === "n") {
          const shortcut = CREATE_SHORTCUTS.find((s) => s.key === key);
          if (shortcut) {
            // For now, navigate to the module's create page
            const routes: Record<string, string> = {
              "create:contact": "/crm/contacts",
              "create:deal": "/crm/deals",
              "create:campaign": "/email/campaigns",
              "create:page": "/pages/ai-generate",
              "create:workflow": "/workflows/automations",
              "create:task": "/crm/tasks",
              "create:booking": "/services/bookings/rebooking",
            };
            const route = routes[shortcut.action];
            if (route) navigate(route);
          }
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate, onShowOverlay, clearPrefix]);

  return { showHint, activePrefix };
}
